'use client';

import { c as _c } from "react/compiler-runtime";
import { type HTMLAttributeAnchorTarget } from 'react';
import { Button } from '@/app/components/button';
import { Checkbox } from '@/app/components/forms/checkbox';
import { SplashImage } from '@/app/components/splash/splash-image';
import { SPLASH_DATA_TEST } from '@/app/components/splash/tracking';
import { cn } from '@/app/utils/cn';
import { createDataAttributes } from '@/app/utils/createDataAttributes';
import type { SplashPopup } from '@/services/splash/types';
const SPLASH_IMAGE_WRAPPER_CLASS = 'aspect-3-4 lg:aspect-16-9 relative block w-full rounded-t-sm lg:rounded-t-md';
const SPLASH_BUTTON_CLASS = 'w-full min-w-auto sm:min-w-39 md:w-full';
export interface SplashUIProps {
  splash: SplashPopup;
  dontShowAgain: boolean;
  loadImageImmediately: boolean;
  onDontShowAgainChange: (next: boolean) => void;
  onEnter: (area: 'image' | 'button') => void;
  onNavigate: (area: 'image' | 'button') => void;
}
export const SplashUI = t0 => {
  const $ = _c(40);
  const {
    splash,
    dontShowAgain,
    loadImageImmediately,
    onDontShowAgainChange,
    onEnter,
    onNavigate
  } = t0;
  const ctaButton = splash.showCta ? splash.ctaButton : undefined;
  const ctaTarget = ctaButton?.target as HTMLAttributeAnchorTarget | undefined;
  const ctaUrl = ctaButton?.url ?? "";
  const ctaLabel = ctaButton?.label ?? "";
  const canShowCta = !!ctaButton && !!ctaLabel;
  let t1;
  if ($[0] !== loadImageImmediately || $[1] !== splash) {
    t1 = <SplashImage splash={splash} loadImageImmediately={loadImageImmediately} />;
    $[0] = loadImageImmediately;
    $[1] = splash;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const image = t1;
  let t2;
  if ($[3] !== canShowCta || $[4] !== ctaTarget || $[5] !== ctaUrl || $[6] !== image || $[7] !== onEnter || $[8] !== onNavigate) {
    t2 = canShowCta ? <a className={SPLASH_IMAGE_WRAPPER_CLASS} href={ctaUrl} target={ctaTarget} rel={ctaTarget === "_blank" ? "noopener noreferrer" : undefined} {...createDataAttributes({
      test: SPLASH_DATA_TEST.imageWrapper
    })} onClick={() => onNavigate("image")}>{image}</a> : <button className={cn(SPLASH_IMAGE_WRAPPER_CLASS, "cursor-pointer")} type="button" {...createDataAttributes({
      test: SPLASH_DATA_TEST.imageWrapper
    })} onClick={() => onEnter("image")}>{image}</button>;
    $[3] = canShowCta;
    $[4] = ctaTarget;
    $[5] = ctaUrl;
    $[6] = image;
    $[7] = onEnter;
    $[8] = onNavigate;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  const imageWrapper = t2;
  let t3;
  if ($[10] !== onDontShowAgainChange) {
    t3 = e => {
      onDontShowAgainChange(e.target.checked);
    };
    $[10] = onDontShowAgainChange;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  const handleOnAcceptedChange = t3;
  let t4;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = createDataAttributes({
      test: SPLASH_DATA_TEST.buttonWrapper
    });
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  const t5 = canShowCta ? "secondary" : "primary";
  let t6;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = createDataAttributes({
      test: SPLASH_DATA_TEST.homeButton
    });
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== onEnter) {
    t7 = () => onEnter("button");
    $[14] = onEnter;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] !== t5 || $[17] !== t7) {
    t8 = <Button className={SPLASH_BUTTON_CLASS} color="black" variant={t5} {...t6} onClick={t7}>เข้าสู่เว็บไซต์</Button>;
    $[16] = t5;
    $[17] = t7;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  let t9;
  if ($[19] !== canShowCta || $[20] !== ctaLabel || $[21] !== ctaTarget || $[22] !== ctaUrl || $[23] !== onNavigate) {
    t9 = !!canShowCta && <Button className={SPLASH_BUTTON_CLASS} color="black" variant="primary" href={ctaUrl} target={ctaTarget} rel={ctaTarget === "_blank" ? "noopener noreferrer" : undefined} {...createDataAttributes({
      test: SPLASH_DATA_TEST.ctaButton
    })} onClick={() => onNavigate("button")}>{ctaLabel}</Button>;
    $[19] = canShowCta;
    $[20] = ctaLabel;
    $[21] = ctaTarget;
    $[22] = ctaUrl;
    $[23] = onNavigate;
    $[24] = t9;
  } else {
    t9 = $[24];
  }
  let t10;
  if ($[25] !== t8 || $[26] !== t9) {
    t10 = <div className="xs:gap-x-4 flex gap-x-2 lg:gap-x-6" {...t4}>{t8}{t9}</div>;
    $[25] = t8;
    $[26] = t9;
    $[27] = t10;
  } else {
    t10 = $[27];
  }
  let t11;
  if ($[28] !== dontShowAgain) {
    t11 = [{
      name: "hide",
      label: "\u0E44\u0E21\u0E48\u0E15\u0E49\u0E2D\u0E07\u0E41\u0E2A\u0E14\u0E07\u0E2D\u0E35\u0E01",
      checked: dontShowAgain
    }];
    $[28] = dontShowAgain;
    $[29] = t11;
  } else {
    t11 = $[29];
  }
  let t12;
  if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = createDataAttributes({
      test: SPLASH_DATA_TEST.checkbox
    });
    $[30] = t12;
  } else {
    t12 = $[30];
  }
  let t13;
  if ($[31] !== handleOnAcceptedChange || $[32] !== t11) {
    t13 = <div><Checkbox name="splash-accepted" options={t11} {...t12} onChangeInput={handleOnAcceptedChange} /></div>;
    $[31] = handleOnAcceptedChange;
    $[32] = t11;
    $[33] = t13;
  } else {
    t13 = $[33];
  }
  let t14;
  if ($[34] !== t10 || $[35] !== t13) {
    t14 = <div className="xs:p-4 flex flex-col gap-2 bg-white p-2 pt-4 lg:flex-row-reverse lg:justify-between">{t10}{t13}</div>;
    $[34] = t10;
    $[35] = t13;
    $[36] = t14;
  } else {
    t14 = $[36];
  }
  let t15;
  if ($[37] !== imageWrapper || $[38] !== t14) {
    t15 = <>{imageWrapper}{t14}</>;
    $[37] = imageWrapper;
    $[38] = t14;
    $[39] = t15;
  } else {
    t15 = $[39];
  }
  return t15;
};